import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import {
  ClubCounterAdjustmentResult,
  ClubCounterSnapshotPlayer,
  ClubFinancePlayerPosition,
} from '../types';
import { formatCurrency, formatDateTime, getMemberRoleLabel } from '../utils/formatters';

type CounterPlayerView = ClubCounterSnapshotPlayer & {
  live_result: number;
  active_tables: number;
  open_receivable: number;
  open_payable: number;
  net_exposure: number;
  last_financial_activity_at: string | null;
  last_game_activity_at: string | null;
};

const getCounterMetricTone = (
  kind: 'wallet' | 'live' | 'receivable' | 'payable' | 'exposure',
  value: number
): string => {
  switch (kind) {
    case 'wallet':
      return value > 0 ? 'portal-counter-stat--positive' : 'portal-counter-stat--neutral';
    case 'live':
      return value > 0
        ? 'portal-counter-stat--positive'
        : value < 0
          ? 'portal-counter-stat--danger'
          : 'portal-counter-stat--neutral';
    case 'receivable':
      return value > 0 ? 'portal-counter-stat--warning' : 'portal-counter-stat--neutral';
    case 'payable':
      return value > 0 ? 'portal-counter-stat--danger' : 'portal-counter-stat--neutral';
    case 'exposure':
      return value > 0
        ? 'portal-counter-stat--warning'
        : value < 0
          ? 'portal-counter-stat--danger'
          : 'portal-counter-stat--neutral';
    default:
      return 'portal-counter-stat--neutral';
  }
};

const getCounterStatusToneClass = (
  status: CounterPlayerView['status']
): 'portal-status-pill--positive' | 'portal-status-pill--warning' | 'portal-status-pill--danger' => {
  switch (status) {
    case 'ACTIVE':
      return 'portal-status-pill--positive';
    case 'INACTIVE':
      return 'portal-status-pill--warning';
    case 'BANNED':
      return 'portal-status-pill--danger';
    default:
      return 'portal-status-pill--warning';
  }
};

const getCounterStatusLabel = (status: CounterPlayerView['status']): string => {
  switch (status) {
    case 'ACTIVE':
      return 'Ativo';
    case 'INACTIVE':
      return 'Inativo';
    case 'BANNED':
      return 'Banido';
    default:
      return status;
  }
};

const getCounterRoleToneClass = (
  role: CounterPlayerView['role']
): 'portal-role-pill--owner' | 'portal-role-pill--admin' | 'portal-role-pill--mod' | 'portal-role-pill--member' => {
  switch (role) {
    case 'OWNER':
      return 'portal-role-pill--owner';
    case 'ADMIN':
      return 'portal-role-pill--admin';
    case 'MOD':
      return 'portal-role-pill--mod';
    case 'MEMBER':
      return 'portal-role-pill--member';
    default:
      return 'portal-role-pill--member';
  }
};

const CounterPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [players, setPlayers] = useState<ClubCounterSnapshotPlayer[]>([]);
  const [financePlayers, setFinancePlayers] = useState<ClubFinancePlayerPosition[]>([]);
  const [search, setSearch] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [operation, setOperation] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [amount, setAmount] = useState('50');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState<ClubCounterAdjustmentResult | null>(null);

  const loadSnapshot = useCallback(async () => {
    if (!selectedClub?.id) {
      setPlayers([]);
      setFinancePlayers([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [snapshot, financePositions] = await Promise.all([
        clubService.getCounterSnapshot(selectedClub.id),
        clubService.getFinancePlayers(selectedClub.id),
      ]);

      setPlayers(snapshot.players || []);
      setFinancePlayers(financePositions || []);
      setTargetUserId((current) => {
        const nextPlayers = snapshot.players || [];
        if (current && nextPlayers.some((player) => player.user_id === current)) {
          return current;
        }

        return nextPlayers[0]?.user_id || '';
      });
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar contador do club');
    } finally {
      setLoading(false);
    }
  }, [selectedClub?.id]);

  useEffect(() => {
    setReceipt(null);
    setSearch('');
    setTargetUserId('');
    loadSnapshot();
  }, [loadSnapshot]);

  const playerMap = useMemo(() => {
    const map = new Map<string, ClubFinancePlayerPosition>();
    financePlayers.forEach((player) => {
      map.set(player.user_id, player);
    });
    return map;
  }, [financePlayers]);

  const counterPlayers = useMemo<CounterPlayerView[]>(() => {
    return players.map((player) => {
      const finance = playerMap.get(player.user_id);

      return {
        ...player,
        live_result: finance?.live_result || 0,
        active_tables: finance?.active_tables || 0,
        open_receivable: finance?.open_receivable || 0,
        open_payable: finance?.open_payable || 0,
        net_exposure: finance?.net_exposure || 0,
        last_financial_activity_at: finance?.last_financial_activity_at || null,
        last_game_activity_at: finance?.last_game_activity_at || null,
      };
    });
  }, [players, playerMap]);

  const filteredPlayers = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return counterPlayers;
    return counterPlayers.filter((player) => player.user_name.toLowerCase().includes(normalized));
  }, [counterPlayers, search]);

  const selectablePlayers = useMemo(() => {
    if (!targetUserId) {
      return filteredPlayers;
    }

    const selected = counterPlayers.find((player) => player.user_id === targetUserId);
    const isAlreadyVisible = filteredPlayers.some((player) => player.user_id === targetUserId);

    if (!selected || isAlreadyVisible) {
      return filteredPlayers;
    }

    return [selected, ...filteredPlayers];
  }, [counterPlayers, filteredPlayers, targetUserId]);

  const selectedPlayer = useMemo(
    () => counterPlayers.find((player) => player.user_id === targetUserId) || null,
    [counterPlayers, targetUserId]
  );

  const numericAmount = Number.parseFloat(amount.replace(',', '.'));
  const debitLimit = selectedPlayer?.wallet_balance || 0;
  const exceedsDebitLimit =
    operation === 'DEBIT' &&
    Number.isFinite(numericAmount) &&
    numericAmount > Number((debitLimit + 0.0001).toFixed(4));

  const canSubmit =
    !submitting &&
    !loading &&
    !!targetUserId &&
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    !exceedsDebitLimit;

  const operationMeta = useMemo(() => {
    if (operation === 'CREDIT') {
      return {
        title: 'Credito operacional',
        description: 'Aumenta a carteira do membro e registra um valor a receber do club.',
        badge: 'Entrada de fichas',
        containerClass: 'portal-counter-operation-meta portal-counter-operation-meta--credit',
      };
    }

    return {
      title: 'Debito operacional',
      description: 'Retira saldo da carteira e registra um valor a pagar do club, respeitando o saldo atual.',
      badge: 'Retirada de fichas',
      containerClass: 'portal-counter-operation-meta portal-counter-operation-meta--debit',
    };
  }, [operation]);

  const operationalHints = useMemo(() => {
    if (!selectedPlayer) {
      return [];
    }

    const hints: string[] = [];

    if (selectedPlayer.open_receivable > 0) {
      hints.push(`Ja existe ${formatCurrency(selectedPlayer.open_receivable)} a receber deste jogador.`);
    }

    if (selectedPlayer.open_payable > 0) {
      hints.push(`O club ja deve ${formatCurrency(selectedPlayer.open_payable)} para este jogador.`);
    }

    if (selectedPlayer.active_tables > 0) {
      hints.push(
        `Jogador esta ativo em ${selectedPlayer.active_tables} mesa(s) com saldo vivo de ${formatCurrency(selectedPlayer.live_result)}.`
      );
    }

    if (selectedPlayer.wallet_balance <= 0) {
      hints.push('A carteira atual esta zerada. Nao ha margem para debito no contador.');
    } else if (operation === 'DEBIT') {
      hints.push(`Debito maximo permitido agora: ${formatCurrency(selectedPlayer.wallet_balance)}.`);
    }

    if (hints.length === 0) {
      hints.push('Sem pendencias financeiras relevantes para este membro no momento.');
    }

    return hints;
  }, [operation, selectedPlayer]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedClub?.id) return;

    if (!targetUserId) {
      setError('Selecione um jogador');
      return;
    }
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError('Informe um valor valido');
      return;
    }
    if (operation === 'DEBIT' && numericAmount > debitLimit) {
      setError(`Debito acima do saldo disponivel em carteira (${formatCurrency(debitLimit)})`);
      return;
    }

    setSubmitting(true);
    setError('');
    setReceipt(null);

    try {
      const result = await clubService.adjustCounter(selectedClub.id, {
        targetUserId,
        operation,
        amount: Number(numericAmount.toFixed(2)),
        reason,
      });
      setReceipt(result);
      setReason('');
      await loadSnapshot();
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao processar ajuste no contador');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-page-grid portal-page-grid--two-columns">
      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Operacao owner-only</p>
            <h3>Contador do club</h3>
          </div>
          <input
            type="search"
            className="portal-inline-search"
            placeholder="Buscar membro por nome"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {selectedPlayer ? (
          <div className="portal-counter-summary">
            <div className="portal-counter-summary__header">
              <div>
                <strong>{selectedPlayer.user_name}</strong>
                <div className="portal-counter-summary__pills">
                  <span className={`portal-role-pill ${getCounterRoleToneClass(selectedPlayer.role)}`}>
                    {getMemberRoleLabel(selectedPlayer.role)}
                  </span>
                  <span className={`portal-status-pill ${getCounterStatusToneClass(selectedPlayer.status)}`}>
                    {getCounterStatusLabel(selectedPlayer.status)}
                  </span>
                </div>
              </div>
              <span className="portal-chip">
                Debito disponivel: {formatCurrency(selectedPlayer.wallet_balance)}
              </span>
            </div>

            <div className="portal-counter-stats">
              <article className={`portal-counter-stat ${getCounterMetricTone('wallet', selectedPlayer.wallet_balance)}`}>
                <span>Carteira</span>
                <strong>{formatCurrency(selectedPlayer.wallet_balance)}</strong>
              </article>
              <article className={`portal-counter-stat ${getCounterMetricTone('live', selectedPlayer.live_result)}`}>
                <span>Saldo vivo</span>
                <strong>{formatCurrency(selectedPlayer.live_result)}</strong>
              </article>
              <article className={`portal-counter-stat ${getCounterMetricTone('receivable', selectedPlayer.open_receivable)}`}>
                <span>A receber</span>
                <strong>{formatCurrency(selectedPlayer.open_receivable)}</strong>
              </article>
              <article className={`portal-counter-stat ${getCounterMetricTone('payable', selectedPlayer.open_payable)}`}>
                <span>A pagar</span>
                <strong>{formatCurrency(selectedPlayer.open_payable)}</strong>
              </article>
              <article className={`portal-counter-stat ${getCounterMetricTone('exposure', selectedPlayer.net_exposure)}`}>
                <span>Exposicao</span>
                <strong>{formatCurrency(selectedPlayer.net_exposure)}</strong>
              </article>
              <article className="portal-counter-stat portal-counter-stat--neutral">
                <span>Mesas ativas</span>
                <strong>{selectedPlayer.active_tables}</strong>
              </article>
            </div>

            <div className="portal-counter-hints">
              <div>
                <span>Ultima atividade financeira</span>
                <strong>{formatDateTime(selectedPlayer.last_financial_activity_at)}</strong>
              </div>
              <div>
                <span>Ultima atividade de jogo</span>
                <strong>{formatDateTime(selectedPlayer.last_game_activity_at)}</strong>
              </div>
            </div>

            <ul className="portal-list portal-counter-list">
              {operationalHints.map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <form className="portal-form-grid" onSubmit={handleSubmit}>
          <label>
            <span>Jogador</span>
            <select
              value={targetUserId}
              onChange={(event) => setTargetUserId(event.target.value)}
              disabled={submitting || loading}
            >
              <option value="">Selecione</option>
              {selectablePlayers.map((player) => (
                <option key={player.user_id} value={player.user_id}>
                  {player.user_name}
                </option>
              ))}
            </select>
          </label>

          <div className="portal-form-grid__full portal-counter-mode">
            <span className="portal-counter-mode__label">Operacao</span>
            <div className="portal-counter-mode__actions" role="group" aria-label="Operacao do contador">
              <button
                type="button"
                className={`portal-counter-mode__button ${operation === 'CREDIT' ? 'portal-counter-mode__button--credit-active' : 'portal-counter-mode__button--idle'}`}
                onClick={() => setOperation('CREDIT')}
                disabled={submitting}
              >
                <strong>Credito</strong>
                <span>Libera saldo na carteira</span>
              </button>
              <button
                type="button"
                className={`portal-counter-mode__button ${operation === 'DEBIT' ? 'portal-counter-mode__button--debit-active' : 'portal-counter-mode__button--idle'}`}
                onClick={() => setOperation('DEBIT')}
                disabled={submitting}
              >
                <strong>Debito</strong>
                <span>Retira saldo da carteira</span>
              </button>
            </div>
          </div>

          <div className="portal-form-grid__full">
            <div className={operationMeta.containerClass}>
              <div>
                <p className="portal-counter-operation-meta__eyebrow">{operationMeta.badge}</p>
                <strong>{operationMeta.title}</strong>
              </div>
              <p>{operationMeta.description}</p>
            </div>
          </div>

          <label>
            <span>Valor</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              max={operation === 'DEBIT' ? Number(debitLimit.toFixed(2)) : undefined}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              disabled={submitting}
            />
            <small className="portal-form-helper">
              {operation === 'DEBIT'
                ? `Debito limitado ao saldo em carteira: ${formatCurrency(debitLimit)}`
                : 'Credito aumenta a carteira e gera lancamento financeiro a receber.'}
            </small>
          </label>

          <label className="portal-form-grid__full">
            <span>Motivo</span>
            <input
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={120}
              disabled={submitting}
              placeholder="Ex.: liberar stack inicial ou recolher sobra de carteira"
            />
          </label>

          {exceedsDebitLimit ? (
            <div className="portal-banner portal-banner--warning portal-form-grid__full">
              O debito nao pode ultrapassar o saldo em carteira do membro.
            </div>
          ) : null}

          {error ? <div className="portal-banner portal-banner--error portal-form-grid__full">{error}</div> : null}

          <div className="portal-form-grid__full portal-form-grid__actions">
            <button type="submit" className="portal-primary-button" disabled={!canSubmit}>
              {submitting ? 'Processando...' : 'Enviar ajuste'}
            </button>
          </div>
        </form>

        {receipt ? (
          <div className={`portal-receipt ${receipt.operation === 'DEBIT' ? 'portal-receipt--debit' : 'portal-receipt--credit'}`}>
            <h4>Recibo gerado</h4>
            <p>Codigo: {receipt.processing_code}</p>
            <p>Jogador: {receipt.target_user_name}</p>
            <p>Operacao: {receipt.operation === 'CREDIT' ? 'Credito' : 'Debito'}</p>
            <p>Valor: {formatCurrency(receipt.amount)}</p>
            <p>Saldo anterior: {formatCurrency(receipt.previous_balance)}</p>
            <p>Saldo novo: {formatCurrency(receipt.new_balance)}</p>
            {receipt.finance_direction ? (
              <p>
                Financeiro: {receipt.finance_direction === 'RECEIVABLE' ? 'A receber' : 'A pagar'} |
                {' '}em aberto {formatCurrency(receipt.finance_outstanding_amount || 0)}
              </p>
            ) : null}
            <p>Processado em: {formatDateTime(receipt.processed_at)}</p>
          </div>
        ) : null}
      </section>

      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Snapshot real</p>
            <h3>Carteiras e contexto financeiro</h3>
          </div>
          <span className="portal-chip">{filteredPlayers.length} membro(s) no filtro</span>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Papel</th>
                <th>Status</th>
                <th>Carteira</th>
                <th>Saldo vivo</th>
                <th>A receber</th>
                <th>A pagar</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}>Carregando contador...</td></tr>
              ) : filteredPlayers.length === 0 ? (
                <tr><td colSpan={7}>Nenhum jogador encontrado.</td></tr>
              ) : (
                filteredPlayers.map((player) => (
                  <tr
                    key={player.user_id}
                    className={player.user_id === targetUserId ? 'portal-table__row--active portal-table__row--clickable' : 'portal-table__row--clickable'}
                    onClick={() => setTargetUserId(player.user_id)}
                  >
                    <td>{player.user_name}</td>
                    <td>
                      <span className={`portal-role-pill ${getCounterRoleToneClass(player.role)}`}>
                        {getMemberRoleLabel(player.role)}
                      </span>
                    </td>
                    <td>
                      <span className={`portal-status-pill ${getCounterStatusToneClass(player.status)}`}>
                        {getCounterStatusLabel(player.status)}
                      </span>
                    </td>
                    <td>{formatCurrency(player.wallet_balance)}</td>
                    <td>{formatCurrency(player.live_result)}</td>
                    <td>{formatCurrency(player.open_receivable)}</td>
                    <td>{formatCurrency(player.open_payable)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default CounterPage;
